<template>
  <div class="license-page">
    <div class="page-head">
      <div>
        <div class="page-title">激活码生成</div>
        <div class="page-subtitle">按 SKU 生成激活码，套餐、平台、时长和设备数都来自 SKU 配置。</div>
      </div>
      <el-button icon="el-icon-refresh" :loading="loadingSkus" @click="readSkus">刷新 SKU</el-button>
    </div>

    <div class="content-grid">
      <section class="panel">
        <div class="panel-title">生成参数</div>
        <el-form :model="form" label-width="110px">
          <el-form-item label="生成方式">
            <el-radio-group v-model="form.mode">
              <el-radio-button label="sku">选择 SKU</el-radio-button>
              <el-radio-button label="manual">手动匹配</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <template v-if="form.mode === 'sku'">
            <div class="sku-picker">
              <div class="sku-picker-head">
                <div>
                  <div class="sku-picker-title">选择 SKU</div>
                  <div class="sku-picker-desc">{{selectedSkuSummary}}</div>
                </div>
                <span class="sku-count">{{filteredSkuList.length}} / {{skuList.length}}</span>
              </div>

              <div class="sku-filters">
                <div class="filter-row">
                  <span class="filter-label">平台</span>
                  <div class="filter-options">
                    <button
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.platform === ''}"
                      @click="setSkuFilter('platform', '')"
                    >
                      全部
                    </button>
                    <button
                      v-for="item in platformFilterOptions"
                      :key="item.value"
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.platform === item.value}"
                      @click="setSkuFilter('platform', item.value)"
                    >
                      {{item.label}}
                    </button>
                  </div>
                </div>
                <div class="filter-row">
                  <span class="filter-label">时长</span>
                  <div class="filter-options">
                    <button
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.time_limit === ''}"
                      @click="setSkuFilter('time_limit', '')"
                    >
                      全部
                    </button>
                    <button
                      v-for="item in timeLimitFilterOptions"
                      :key="item.value"
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.time_limit === item.value}"
                      @click="setSkuFilter('time_limit', item.value)"
                    >
                      {{item.label}}
                    </button>
                  </div>
                </div>
                <div class="filter-row">
                  <span class="filter-label">设备</span>
                  <div class="filter-options">
                    <button
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.device_num === ''}"
                      @click="setSkuFilter('device_num', '')"
                    >
                      全部
                    </button>
                    <button
                      v-for="item in deviceFilterOptions"
                      :key="item.value"
                      type="button"
                      class="filter-chip"
                      :class="{active: skuFilters.device_num === item.value}"
                      @click="setSkuFilter('device_num', item.value)"
                    >
                      {{item.label}}
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="isSkuFiltered" class="filter-summary">
                <span>已筛选 {{filteredSkuList.length}} 项</span>
                <button type="button" @click="clearSkuFilters">清除筛选</button>
              </div>

              <div v-if="skuList.length === 0" class="empty-state">暂无可用 SKU</div>
              <div v-else-if="filteredSkuList.length === 0" class="empty-state">无匹配 SKU</div>
              <div v-else class="sku-list">
                <button
                  v-for="sku in filteredSkuList"
                  :key="sku.id"
                  type="button"
                  class="sku-row"
                  :class="{active: String(sku.id) === String(form.sku_id)}"
                  @click="selectSku(sku)"
                >
                  <span class="check-mark">
                    <i v-if="String(sku.id) === String(form.sku_id)" class="el-icon-check"></i>
                  </span>
                  <span class="sku-main">
                    <span class="sku-name">{{sku.sku_name || sku.sku_key}}</span>
                    <span class="sku-key">{{sku.sku_key}}</span>
                  </span>
                  <span class="sku-tags">
                    <span>{{platformLabel(sku.platform)}}</span>
                    <span>{{timeLimitText(sku.time_limit)}}</span>
                    <span>{{deviceText(sku.allow_device_num)}}</span>
                    <span>{{sku.plan_key || '-'}}</span>
                  </span>
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <el-form-item label="平台">
              <el-select v-model="form.platform" class="full-width">
                <el-option label="Windows" value="win32" />
                <el-option label="macOS" value="darwin" />
                <el-option label="Windows + macOS" value="all" />
                <el-option label="Android" value="android" />
                <el-option label="iOS" value="ios" />
              </el-select>
            </el-form-item>
            <el-form-item label="有效期">
              <el-select v-model="form.time_limit" class="full-width">
                <el-option label="永久" :value="0" />
                <el-option label="7天" :value="7" />
                <el-option label="30天" :value="30" />
                <el-option label="120天" :value="120" />
                <el-option label="180天" :value="180" />
                <el-option label="365天" :value="365" />
              </el-select>
            </el-form-item>
            <el-form-item label="允许设备数">
              <el-input-number v-model="form.allow_device_num" :min="1" :max="100" />
            </el-form-item>
            <div class="form-help">手动模式不会绕过 SKU，提交前必须匹配到一个已启用 SKU。</div>
          </template>

          <el-form-item label="数量">
            <el-input-number v-model="form.create_num" :min="1" :max="300" />
          </el-form-item>
        </el-form>
        <div class="panel-actions">
          <el-button type="primary" icon="el-icon-plus" :loading="creating" @click="createCodes">生成激活码</el-button>
          <el-button icon="el-icon-search" :loading="loadingCodes" @click="getUnusedCodes">查询未使用码</el-button>
        </div>
      </section>

      <section class="panel">
        <div class="panel-title-row">
          <div>
            <div class="panel-title">结果</div>
            <div class="panel-subtitle">生成或查询结果会显示在这里。</div>
          </div>
          <el-button size="mini" icon="el-icon-document-copy" @click="copyCodes">复制 code 列</el-button>
        </div>
        <el-table :data="codes" border height="520">
          <el-table-column prop="code" label="激活码" min-width="260" />
          <el-table-column prop="sku_key" label="SKU" min-width="170" />
          <el-table-column prop="plan_key" label="套餐" width="110" />
          <el-table-column prop="plan_mode" label="权益模式" width="110" />
          <el-table-column label="平台" width="110">
            <template slot-scope="scope">{{platformLabel(scope.row.platform)}}</template>
          </el-table-column>
          <el-table-column label="有效期" width="100">
            <template slot-scope="scope">{{timeLimitText(scope.row.time_limit)}}</template>
          </el-table-column>
          <el-table-column label="设备数" width="90">
            <template slot-scope="scope">{{deviceText(scope.row.allow_device_num)}}</template>
          </el-table-column>
        </el-table>
      </section>
    </div>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'LicenseActivationCodes',
  data() {
    return {
      creating: false,
      loadingCodes: false,
      loadingSkus: false,
      skuList: [],
      codes: [],
      form: {
        mode: 'sku',
        sku_id: null,
        platform: 'win32',
        time_limit: 365,
        allow_device_num: 1,
        create_num: 1,
      },
      skuFilters: {
        platform: '',
        time_limit: '',
        device_num: '',
      },
    }
  },
  computed: {
    currentSku() {
      const sku = this.skuList.find((item) => String(item.id) === String(this.form.sku_id))
      return sku || {}
    },
    selectedSkuSummary() {
      if (!this.currentSku.id) return '请选择要生成的激活码 SKU'
      return `${this.platformLabel(this.currentSku.platform)} · ${this.timeLimitText(this.currentSku.time_limit)} · ${this.deviceText(this.currentSku.allow_device_num)} · ${this.currentSku.plan_key || '-'}`
    },
    filteredSkuList() {
      return this.skuList.filter((item) => {
        return (!this.skuFilters.platform || String(item.platform) === this.skuFilters.platform) &&
          (!this.skuFilters.time_limit || String(Number(item.time_limit)) === this.skuFilters.time_limit) &&
          (!this.skuFilters.device_num || String(Number(item.allow_device_num)) === this.skuFilters.device_num)
      })
    },
    platformFilterOptions() {
      return this.uniqueSkuOptions('platform', (item) => item.platform, (value) => this.platformLabel(value))
    },
    timeLimitFilterOptions() {
      return this.uniqueSkuOptions('time_limit', (item) => Number(item.time_limit), (value) => this.timeLimitText(value))
    },
    deviceFilterOptions() {
      return this.uniqueSkuOptions('allow_device_num', (item) => Number(item.allow_device_num), (value) => this.deviceText(value))
    },
    isSkuFiltered() {
      return this.skuFilters.platform !== '' ||
        this.skuFilters.time_limit !== '' ||
        this.skuFilters.device_num !== ''
    },
  },
  methods: {
    readSkus() {
      this.loadingSkus = true
      apis.activation_code.readSkus({}).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '读取 SKU 失败', type: 'error' })
          return
        }
        this.skuList = body.data || []
        if (!this.form.sku_id && this.skuList.length > 0) {
          this.form.sku_id = this.skuList[0].id
        }
        this.ensureSelectedSkuVisible()
      }).catch((err) => {
        this.$message({ message: String(err || '读取 SKU 失败'), type: 'error' })
      }).finally(() => {
        this.loadingSkus = false
      })
    },
    buildPayload() {
      const sku = this.form.mode === 'sku' ? this.currentSku : this.findManualSku()
      if (!sku || !sku.id) {
        this.$message({ message: '未找到匹配 SKU，请先在 SKU 管理中配置', type: 'warning' })
        return null
      }

      return {
        sku_id: sku.id,
        create_num: this.form.create_num,
        get_num: this.form.create_num,
      }
    },
    findManualSku() {
      return this.skuList.find((item) => {
        return String(item.platform) === String(this.form.platform) &&
          Number(item.time_limit) === Number(this.form.time_limit) &&
          Number(item.allow_device_num) === Number(this.form.allow_device_num)
      })
    },
    selectSku(sku) {
      this.form.sku_id = sku.id
    },
    setSkuFilter(field, value) {
      this.skuFilters[field] = value
      this.ensureSelectedSkuVisible()
    },
    clearSkuFilters() {
      this.skuFilters = {
        platform: '',
        time_limit: '',
        device_num: '',
      }
      this.ensureSelectedSkuVisible()
    },
    ensureSelectedSkuVisible() {
      if (this.filteredSkuList.length === 0) {
        this.form.sku_id = null
        return
      }
      const exists = this.filteredSkuList.some((item) => String(item.id) === String(this.form.sku_id))
      if (!exists) {
        this.form.sku_id = this.filteredSkuList[0].id
      }
    },
    createCodes() {
      const payload = this.buildPayload()
      if (!payload) return

      this.creating = true
      apis.activation_code.create(payload).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '生成失败', type: 'error' })
          return
        }
        this.codes = body.data || []
        this.$message({ message: '生成成功', type: 'success' })
      }).catch((err) => {
        this.$message({ message: String(err || '生成失败'), type: 'error' })
      }).finally(() => {
        this.creating = false
      })
    },
    getUnusedCodes() {
      const payload = this.buildPayload()
      if (!payload) return

      this.loadingCodes = true
      apis.activation_code.readUnused(payload).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '查询失败', type: 'error' })
          return
        }
        this.codes = body.data || []
      }).catch((err) => {
        this.$message({ message: String(err || '查询失败'), type: 'error' })
      }).finally(() => {
        this.loadingCodes = false
      })
    },
    uniqueSkuOptions(field, getValue, getLabel) {
      const values = {}
      this.skuList.forEach((item) => {
        const rawValue = getValue(item)
        const value = String(rawValue)
        values[value] = {
          value,
          rawValue,
          label: getLabel(rawValue),
        }
      })
      return Object.keys(values).map((key) => values[key]).sort((a, b) => {
        if (field === 'platform') {
          return String(a.label).localeCompare(String(b.label))
        }
        return Number(a.rawValue) - Number(b.rawValue)
      })
    },
    platformLabel(platform) {
      const labels = {
        win32: 'Windows',
        darwin: 'macOS',
        all: 'Windows + macOS',
        android: 'Android',
        ios: 'iOS',
      }
      return labels[platform] || platform || '-'
    },
    timeLimitText(timeLimit) {
      const value = Number(timeLimit)
      if (value === 0) return '永久'
      if (value === 0.5) return '12小时'
      if (value === 30) return '1个月'
      if (value === 120) return '季度'
      if (value === 365) return '1年'
      return `${value}天`
    },
    deviceText(deviceNum) {
      const value = Number(deviceNum)
      return value > 0 ? `${value}台` : '-'
    },
    copyCodes() {
      const text = this.codes.map((item) => item.code).filter(Boolean).join('\n')
      if (!text) {
        this.$message({ message: '没有可复制的激活码', type: 'warning' })
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message({ message: '已复制', type: 'success' })
        }).catch(() => {
          this.fallbackCopy(text)
        })
        return
      }
      this.fallbackCopy(text)
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$message({ message: '已复制', type: 'success' })
    },
  },
  mounted() {
    this.readSkus()
  },
}
</script>

<style lang="scss" scoped>
.license-page {
  padding: 18px;
  background: #f5f7fa;
  min-height: calc(100vh - 36px);
}
.page-head,
.panel-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  font-size: 24px;
  color: #1f2d3d;
  line-height: 32px;
}
.page-subtitle,
.panel-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-top: 4px;
}
.content-grid {
  display: grid;
  grid-template-columns: 520px 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 18px;
}
.panel-title {
  font-size: 16px;
  color: #1f2d3d;
  margin-bottom: 14px;
}
.panel-actions {
  display: flex;
  gap: 8px;
  padding-left: 110px;
}
.full-width {
  width: 100%;
}
.sku-picker {
  margin-bottom: 18px;
}
.sku-picker-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.sku-picker-title {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}
.sku-picker-desc {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
}
.sku-count {
  flex: 0 0 auto;
  min-width: 56px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f2f4f7;
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}
.sku-filters {
  display: grid;
  gap: 8px;
  margin-bottom: 10px;
}
.filter-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}
.filter-label {
  color: #7b8794;
  font-size: 13px;
}
.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.filter-chip {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #d8dee6;
  border-radius: 999px;
  background: #fff;
  color: #52606d;
  font-size: 12px;
  line-height: 26px;
  cursor: pointer;
}
.filter-chip.active {
  border-color: #409eff;
  background: #ecf5ff;
  color: #1f6fb2;
  font-weight: 600;
}
.filter-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #7b8794;
  font-size: 12px;
  line-height: 20px;
}
.filter-summary button {
  border: 0;
  background: transparent;
  color: #409eff;
  cursor: pointer;
}
.empty-state {
  padding: 26px 0;
  border: 1px dashed #d8dee6;
  border-radius: 6px;
  color: #909399;
  font-size: 13px;
  text-align: center;
}
.sku-list {
  display: grid;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 2px;
}
.sku-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.sku-row.active {
  border-color: #409eff;
  background: #f4f9ff;
}
.check-mark {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 1px solid #c9d1d9;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}
.sku-row.active .check-mark {
  border-color: #409eff;
  background: #409eff;
}
.sku-main {
  min-width: 0;
}
.sku-name {
  display: block;
  color: #1f2d3d;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}
.sku-key {
  display: block;
  margin-top: 2px;
  color: #9aa5b1;
  font-size: 12px;
  line-height: 18px;
  word-break: break-all;
}
.sku-tags {
  display: flex;
  flex-wrap: wrap;
  grid-column: 2;
  gap: 6px;
  margin-top: 7px;
}
.sku-tags span {
  padding: 2px 7px;
  border-radius: 999px;
  background: #eef2f7;
  color: #52606d;
  font-size: 12px;
  line-height: 18px;
}
.form-help {
  margin: -4px 0 18px 110px;
  color: #909399;
  font-size: 12px;
  line-height: 18px;
}
@media (max-width: 1180px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
